import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import AnchoredPopover from './AnchoredPopover'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2, AlertTriangle, AlertCircle, Loader2, Send, RotateCw, Bot, Maximize2,
  ExternalLink, Trash2, Plus, Calculator,
  FileText, FileSpreadsheet, ChevronDown, ChevronRight, XCircle, X,
  ClipboardList, Activity, Sparkles, History, Layers, RefreshCw, Check, Clock, MapPin, CornerDownRight,
} from 'lucide-react'
import PDFViewer from './PDFViewer'
import CostBreakdown from './CostBreakdown'
import RequestPanelTabs from './requestDetail/RequestPanelTabs'
import FieldsPanelHeader from './requestDetail/FieldsPanelHeader'
import ActivityLogPanel from './requestDetail/ActivityLogPanel'
import EditGate from './requestDetail/EditGate'
import { nsApi, requestsApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { useDisplayPreferences } from '../context/DisplayPreferencesContext'
import { isReadOnlyUser } from '../utils/readerAccess'
import { isRequestLocked } from '../utils/requestStatus'
import { formatAuditLogsForActivity } from '../utils/auditLog'
import { formatDateTime, formatTimeAgo } from '../utils/dateUtils'
import useSegmentedTabIndicator from '../hooks/useSegmentedTabIndicator'
import { VendorField, CustomerField, ItemSearchDropdown } from './NSRequestView'

import { normaliseBidDate } from './bidDates'
import OpCoBadge from './opco/OpCoBadge'
import { MoveOpCoButton, UnassignedOpCoBanner } from './opco/MoveOpCoDialog'

// ─── Vendor Bid field keys ────────────────────────────────────────────────────
// This screen was built for, and originally hardcoded to, Samsung Special
// Pricing's exact live NetSuite custom field keys. That was a real bug: ANY
// vendor_bid template routes here (see RequestDetail.jsx's ns_entity_type
// check), regardless of whose account or which custom fields it actually
// uses — a differently-configured vendor_bid template (confirmed on request
// 102 / template 59, whose fields were tranId/entity/item, not
// custrecord_jcs_sp_*) rendered with every section empty, since none of its
// real field keys matched these literals.
//
// DEFAULT_H / DEFAULT_L below are kept as Samsung's exact values — used only
// as the fallback when a template doesn't define a given logical slot.
// deriveBidKeys() below builds the ACTUAL H/L used by resolving each logical
// slot (project name, item code, ...) against the current request's own
// fields (by field_name for header slots, by line-item column key for line
// slots), falling back to the Samsung default only when the template has no
// field for that slot at all. For Samsung's own templates every field_name
// and column key already matches these role names 1:1, so this produces the
// exact same H/L as before — zero behaviour change for the verified flow.
const DEFAULT_H = {
  project: 'custrecord_project_name',
  bidNumber: 'custrecord_jcs_sp_bids',
  supplier: 'custrecord_jcs_sp_supplier',
  reseller: 'custrecord_jcs_sp_reseller',
  endCustomer: 'custrecord_jcs_sp_end_customer',
  closeDate: 'custrecord_jcs_sp_expected_closed_date',
  email: 'custrecord_jcs_sp_email',
  phone: 'custrecord_jcs_sp_phone',
  postalAddress: 'custrecord_jcs_sp_postal_address',
}

const DEFAULT_L = {
  item: 'custrecord_jcs_spil_item_code',
  quantity: 'custrecord_jcs_spil_quantity',
  listPrice: 'custrecord_jcs_spil_list_price',
  bidPrice: 'custrecord_jcs_spil_requested_price',
  priceLimit: 'custrecord_jcs_spil_2nd_requested_price',
  discount: 'custrecord_jcs_spil_discount',
  amount: 'custrecord_jcs_spil_amount',
  pricingId: 'custrecord_jcs_spil_pricing_id',
  validFrom: 'custrecord_jcs_spil_valid_period_from',
  validTo: 'custrecord_jcs_spil_valid_period_to',
  reseller: 'custrecord_jcs_spil_reseller',
  resellerAddr: 'custrecord_jcs_spil_reseller_postal_add',
  itemNo: 'custrecord_jcs_spil_item_no',
}

// Logical slot -> the field_name (header) / line-item column key (line) a
// correctly-built vendor_bid template uses for it — the same convention
// template_spec_engine.py and the Templates.jsx wizard both already write
// (project_name, opp_no, item_code, valid_period_from, ...).
const HEADER_SLOT_FIELD_NAMES = {
  project: 'project_name', bidNumber: 'opp_no', supplier: 'supplier',
  reseller: 'reseller', endCustomer: 'end_customer', closeDate: 'expected_closed_date',
  email: 'email', phone: 'phone', postalAddress: 'postal_address',
}
const LINE_SLOT_COLUMN_KEYS = {
  item: 'item_code', quantity: 'quantity', listPrice: 'list_price',
  bidPrice: 'requested_price', priceLimit: 'second_requested_price',
  discount: 'discount', amount: 'amount', pricingId: 'pricing_id',
  validFrom: 'valid_period_from', validTo: 'valid_period_to',
  reseller: 'reseller', resellerAddr: 'reseller_postal_address', itemNo: 'item_no',
}

// Resolves this request's ACTUAL H/L from its own fields, instead of assuming
// every vendor_bid template is Samsung Special Pricing.
function deriveBidKeys(fields) {
  const byFieldName = {}
  for (const f of fields || []) {
    if (f.field_name && f.ns_field) byFieldName[f.field_name] = f.ns_field
  }
  const H = { ...DEFAULT_H }
  for (const [slot, fieldName] of Object.entries(HEADER_SLOT_FIELD_NAMES)) {
    if (byFieldName[fieldName]) H[slot] = byFieldName[fieldName]
  }

  let columnsByKey = {}
  const tableField = (fields || []).find(f =>
    ['array_of_objects', 'table'].includes((f.field_type || '').toLowerCase()) ||
    f.field_name === 'line_items'
  )
  if (tableField?.field_values) {
    try {
      const raw = typeof tableField.field_values === 'string'
        ? JSON.parse(tableField.field_values) : tableField.field_values
      if (Array.isArray(raw)) {
        for (const col of raw) {
          const key = col.key || col.name
          if (key && col.ns_field) columnsByKey[key] = col.ns_field
        }
      }
    } catch { /* fall through to defaults */ }
  }
  const L = { ...DEFAULT_L }
  for (const [slot, colKey] of Object.entries(LINE_SLOT_COLUMN_KEYS)) {
    if (columnsByKey[colKey]) L[slot] = columnsByKey[colKey]
  }

  return { H, L }
}

// Line fields a bid document states ONCE in its header and NetSuite wants on
// every line. A line that doesn't carry its own value inherits the header's —
// shown muted as "from header" and included in the posted line payload.
function inheritedLineKeys(L) {
  return [L.pricingId, L.validFrom, L.validTo, L.reseller, L.resellerAddr]
}
function inheritedLabels(L) {
  return {
    [L.pricingId]: 'Pricing ID',
    [L.validFrom]: 'Valid From',
    [L.validTo]: 'Valid To',
    [L.reseller]: 'Reseller',
    [L.resellerAddr]: 'Reseller Address',
  }
}
// Extracted header field names each inherited value may arrive under, when the
// template maps it by name rather than by ns_field.
function headerInheritAliases(L) {
  return {
    [L.pricingId]: ['pricing_id', 'pricing_no', 'pricing_number'],
    [L.validFrom]: ['valid_period_from', 'valid_from', 'valid_period(from)', 'valid_period_(from)'],
    [L.validTo]: ['valid_period_to', 'valid_to', 'valid_period(to)', 'valid_period_(to)'],
    [L.resellerAddr]: ['reseller_postal_address', 'reseller_address', 'reseller_postal_add'],
  }
}

// How the backend matched an extracted product code to a NetSuite item.
const MATCH_SOURCE_LABELS = {
  itemid: 'matched by item code',
  vendorname: 'matched by Samsung part no.',
  mpn: 'matched by manufacturer part no.',
  description: 'matched from description',
}

// Header fields a person fills in, in the order they appear on a bid document.
function buildHeaderFields(H) {
  return [
    { key: H.project, label: 'Project Name', required: true, type: 'text' },
    { key: H.bidNumber, label: 'Bid Number', required: true, type: 'text' },
    { key: H.endCustomer, label: 'End Customer', required: true, type: 'text' },
    { key: H.closeDate, label: 'Expected Close Date', required: true, type: 'date' },
    // Was extracted onto the form but silently dropped when posting — this
    // field never made it into headerPayload because it wasn't in this list.
    { key: H.postalAddress, label: 'Postal Address', required: false, type: 'text' },
    // Reseller Email / Phone deliberately removed, not just left unfilled:
    // Dan's own spec says neither ever appears on a Samsung Special Pricing
    // document. The one email on the doc (e.g. niz.ali@samsung.com) belongs to
    // the Samsung sales rep, not the reseller/end customer — showing an empty
    // "Reseller Email" box next to it invited exactly the confusion Dan
    // flagged. custrecord_jcs_sp_email/_phone stay out of the template's own
    // field list for the same reason (see EXCLUDED_FIELDS in
    // backend/scripts/create_samsung_special_pricing_template.py).
  ]
}

// How a document's extracted line columns map onto the NS line keys. Mirrors the
// column set the template's line_items field is created with.
function buildLineAliases(L) {
  return {
    // 'item_no' is NOT an item alias: on a Samsung bid "Item No" is the line's
    // sequence number (210, 340), a separate NetSuite field (L.itemNo).
    [L.item]: ['item_code', 'product_code', 'code', 'sku', 'part_number', 'number'],
    [L.itemNo]: ['item_no', 'line_no'],
    // Missing until now — the per-row "reseller" column parsed out of a
    // document's line_items table was silently dropped (no alias to find it
    // under), even though the AI extracted it correctly. Confirmed on request
    // 143: line_items[0].reseller = "ENGAGIS PTY LIMITED..." never reached the
    // header Reseller picker because pick(L.reseller) always returned ''.
    [L.reseller]: ['reseller'],
    [L.quantity]: ['quantity', 'qty', 'ordered_qty'],
    [L.listPrice]: ['list_price', 'rrp', 'unit_price', 'price', 'list'],
    [L.bidPrice]: ['bid_price', 'requested_price', 'special_price', 'discounted_price', 'deal_price', 'net_price'],
    [L.validFrom]: ['valid_from', 'valid_period_from', 'start_date', 'from'],
    [L.validTo]: ['valid_to', 'valid_period_to', 'expiry', 'expires', 'end_date', 'to'],
    // Printed per line on Samsung bids; posted as-is (the server only falls
    // back to the bid price when absent).
    [L.priceLimit]: ['second_requested_price', '2nd_requested_price', 'price_limit'],
    [L.pricingId]: ['pricing_id'],
    [L.resellerAddr]: ['reseller_postal_address', 'reseller_address'],
  }
}

function blankLine(L) {
  return {
    [L.item]: '', _itemText: '', _description: '',
    [L.quantity]: '', [L.listPrice]: '', [L.bidPrice]: '',
    [L.validFrom]: '', [L.validTo]: '',
  }
}

function toNumber(v) {
  if (v === null || v === undefined || String(v).trim() === '') return null
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''))
  return isNaN(n) ? null : n
}

// The two values the server always recomputes. Shown here read-only so the user
// can see what will be posted — never as an input, because a typed value would
// be discarded by the server anyway (it recalculates from list/bid/qty).
function computeDiscountPct(line, L) {
  const list = toNumber(line[L.listPrice])
  const bid = toNumber(line[L.bidPrice])
  if (!list || list <= 0 || bid === null) return null
  return Math.round(((list - bid) / list) * 10000) / 100
}
function computeAmount(line, L) {
  const bid = toNumber(line[L.bidPrice])
  const qty = toNumber(line[L.quantity])
  if (bid === null || qty === null) return null
  return Math.round(bid * qty * 100) / 100
}

function fmtMoney(n) {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// ─── Bid line items table ─────────────────────────────────────────────────────

function BidLinesTable({ lines, onChange, items, readOnly, headerInherit = {}, onCellFocus, L }) {
  const update = (idx, patch) => {
    const next = [...lines]
    next[idx] = { ...next[idx], ...patch }
    onChange(next)
  }
  const removeLine = (idx) => onChange(lines.filter((_, i) => i !== idx))
  const INHERITED_LINE_KEYS = inheritedLineKeys(L)
  const INHERITED_LABELS = inheritedLabels(L)

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Bid Line Items</h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">{lines.length} {lines.length === 1 ? 'line' : 'lines'}</span>
        </div>
        {!readOnly && (
          <button
            onClick={() => onChange([...lines, blankLine(L)])}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline transition-transform duration-150 active:scale-95"
          >
            <Plus className="h-3.5 w-3.5" /> Add line
          </button>
        )}
      </div>

      <div className="px-4 py-2 bg-blue-50/60 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/40 flex items-start gap-2">
        <Calculator className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-blue-700 dark:text-blue-300">
          <strong>Discount %</strong> and <strong>Line Amount</strong> are calculated from List Price, Bid Price
          and Quantity — they are always recomputed when posting, so they can't be edited here.
          <strong> Price Limit</strong> is the 2nd Requested Price (the Bid Price when none is printed); <strong>Pricing ID</strong> comes from the bid header.
        </p>
      </div>

      {INHERITED_LINE_KEYS.some(k => headerInherit[k]) && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1 font-medium">
            <CornerDownRight className="h-3 w-3" /> Copied to every line from the header:
          </span>
          {INHERITED_LINE_KEYS.filter(k => headerInherit[k]).map(k => (
            <span key={k}>
              {INHERITED_LABELS[k]} <span className="text-gray-700 dark:text-gray-300">{headerInherit[k]}</span>
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        {/* table-fixed: without it, the browser's auto layout proportionally
            shrinks EVERY column (ignoring the explicit w-[...] widths below)
            whenever the sum of columns is wider than the visible container —
            which is what squeezed Qty/List Price/Bid Price down to 2-3
            visible characters. table-fixed makes column widths authoritative,
            so overflow-x-auto handles any overflow with a scrollbar instead
            of the browser squeezing text. */}
        <table className="w-full text-xs table-fixed">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400">
              <th className="px-3 py-2 text-left font-medium w-8">#</th>
              <th className="px-3 py-2 text-left font-medium w-[170px]">Item <span className="text-red-500">*</span></th>
              <th className="px-3 py-2 text-left font-medium w-16">Qty <span className="text-red-500">*</span></th>
              <th className="px-3 py-2 text-left font-medium w-[136px]">List Price <span className="text-red-500">*</span></th>
              <th className="px-3 py-2 text-left font-medium w-[136px]">Bid Price <span className="text-red-500">*</span></th>
              <th className="px-3 py-2 text-right font-medium w-20 text-gray-400">Disc %</th>
              <th className="px-3 py-2 text-right font-medium w-28 text-gray-400">Amount</th>
              <th className="px-3 py-2 text-left font-medium w-32">Valid From <span className="text-red-500">*</span></th>
              <th className="px-3 py-2 text-left font-medium w-32">Valid To <span className="text-red-500">*</span></th>
              {!readOnly && <th className="w-8" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {lines.map((line, idx) => {
              const disc = computeDiscountPct(line, L)
              const amt = computeAmount(line, L)
              // type="text" + inputMode, not type="number": number inputs can
              // silently render blank while the DOM value is still correct
              // (a known browser quirk with React-controlled number inputs,
              // especially right after a programmatic value set like the
              // extraction-seeding above) — Disc %/Amount computing correctly
              // from these same values proved the data was fine, only the
              // number input's own rendering wasn't showing it.
              // text-left (not text-right): values like "AUD 1200.00" are
              // longer than a compact numeric column can always fit, and
              // right-aligned text in a too-narrow, unfocused input clips
              // from the START — showing a meaningless fragment ("Al", ")0")
              // instead of the actual value. Left-aligned always keeps the
              // beginning of the value visible.
              const numCell = 'w-full px-2 py-1.5 text-left text-sm rounded border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
              const missing = (v) => (v === '' || v === null || v === undefined)
              const border = (v) => missing(v)
                ? 'border-amber-400 dark:border-amber-500'
                : 'border-gray-300 dark:border-gray-600'
              return (
                <tr key={idx} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30"
                  onFocusCapture={() => onCellFocus?.(idx)}
                  onClick={() => onCellFocus?.(idx)}>
                  <td className="px-3 py-2.5 text-gray-400">{idx + 1}</td>
                  <td className="px-3 py-2.5">
                    <ItemSearchDropdown
                      value={line._itemText}
                      extractedText={line._itemText}
                      items={items}
                      matchStatus={line[L.item] ? 'matched' : (line._itemText ? 'unmatched' : 'empty')}
                      placeholder="Search item…"
                      onChange={(sel) => update(idx, {
                        [L.item]: sel?.id || '',
                        _itemText: sel?.number || sel?.name || '',
                        // ItemSearchDropdown's onChange payload only ever carries
                        // `.description` (see its option button below) — `.name`
                        // is never set on it, so reading sel?.name here always
                        // missed and silently kept the old description no matter
                        // which item was picked.
                        _description: sel?.description || line._description,
                        // A hand-picked item is no longer the backend's match.
                        _matchSource: '',
                      })}
                    />
                    {line[L.item] && MATCH_SOURCE_LABELS[line._matchSource] && (
                      <span className="mt-1 inline-block px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-[10px] text-green-700 dark:text-green-400"
                        title={line._sourceCode ? `Document product code: ${line._sourceCode}` : undefined}>
                        {MATCH_SOURCE_LABELS[line._matchSource]}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <input type="text" inputMode="numeric" value={line[L.quantity] ?? ''} disabled={readOnly}
                      onChange={e => {
                        const v = e.target.value
                        if (v === '' || /^\d*$/.test(v)) update(idx, { [L.quantity]: v })
                      }}
                      className={`${numCell} ${border(line[L.quantity])}`} />
                  </td>
                  <td className="px-3 py-2.5">
                    <input type="text" inputMode="decimal" value={line[L.listPrice] ?? ''} disabled={readOnly}
                      onChange={e => {
                        const v = e.target.value
                        if (v === '' || /^\d*\.?\d*$/.test(v)) update(idx, { [L.listPrice]: v })
                      }}
                      className={`${numCell} ${border(line[L.listPrice])}`} />
                  </td>
                  <td className="px-3 py-2.5">
                    <input type="text" inputMode="decimal" value={line[L.bidPrice] ?? ''} disabled={readOnly}
                      onChange={e => {
                        const v = e.target.value
                        if (v === '' || /^\d*\.?\d*$/.test(v)) update(idx, { [L.bidPrice]: v })
                      }}
                      className={`${numCell} ${border(line[L.bidPrice])}`} />
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                    {disc === null ? '—' : `${disc}%`}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                    {fmtMoney(amt)}
                  </td>
                  <td className="px-3 py-2.5">
                    <input type="date" value={line[L.validFrom] || headerInherit[L.validFrom] || ''} disabled={readOnly}
                      onChange={e => update(idx, { [L.validFrom]: e.target.value })}
                      className={`w-full px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-800 ${!line[L.validFrom] && headerInherit[L.validFrom] ? 'text-gray-400 dark:text-gray-500 italic' : 'text-gray-900 dark:text-gray-100'} ${border(line[L.validFrom] || headerInherit[L.validFrom])}`} />
                    {!line[L.validFrom] && headerInherit[L.validFrom] && (
                      <span className="block mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">from header</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <input type="date" value={line[L.validTo] || headerInherit[L.validTo] || ''} disabled={readOnly}
                      onChange={e => update(idx, { [L.validTo]: e.target.value })}
                      className={`w-full px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-800 ${!line[L.validTo] && headerInherit[L.validTo] ? 'text-gray-400 dark:text-gray-500 italic' : 'text-gray-900 dark:text-gray-100'} ${border(line[L.validTo] || headerInherit[L.validTo])}`} />
                    {!line[L.validTo] && headerInherit[L.validTo] && (
                      <span className="block mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">from header</span>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-2 py-2">
                      <button onClick={() => removeLine(idx)} title="Remove line"
                        className="text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
            {lines.length === 0 && (
              <tr><td colSpan={readOnly ? 9 : 10} className="px-3 py-6 text-center text-gray-400">
                No line items yet — add one to post this bid.
              </td></tr>
            )}
          </tbody>
          {lines.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-800/40 font-medium text-gray-700 dark:text-gray-300">
                <td colSpan={6} className="px-3 py-2 text-right">Total bid value</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtMoney(lines.reduce((s, l) => s + (computeAmount(l, L) || 0), 0))}
                </td>
                <td colSpan={readOnly ? 2 : 3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

// Page + extracted text a focused field came from on the PDF.
function SourceHint({ hint }) {
  if (!hint) return null
  return (
    <p className="mt-1 flex items-start gap-1 text-[11px] text-gray-500 dark:text-gray-400 animate-panel-in">
      <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0 text-indigo-400" />
      <span className="min-w-0 truncate" title={hint.text}>
        Source{hint.page ? `: page ${hint.page}` : ''} — <span className="text-gray-700 dark:text-gray-300">"{hint.text}"</span>
      </span>
    </p>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NSBidRequestView({ request, showProcessingModal, processingMinimized,
  setProcessingMinimized, processingProgress = { percent: 0, message: '' }, processingLog = [],
  onExtract, onOpCoMoved }) {
  const toast = useToast()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const isReader = isReadOnlyUser(authUser)
  const isEditable = !isReader && !isRequestLocked(request?.status)
  const notifyLocked = () => toast.info('Read-only', isReader ? 'You have read-only access' : 'This request is finalized and can no longer be edited')
  const orgId = request?.organization_id
  const { showConfidenceScore, showColorCoding } = useDisplayPreferences()

  const fields = useMemo(() =>
    (request?.fields || []).filter(f => f.is_active !== false).map(f => ({
      ...f,
      value: f.value ?? f.field_value ?? '',
    }))
  , [request])

  // This request's ACTUAL NetSuite field keys, resolved from its own template
  // instead of assuming every vendor_bid template is Samsung Special Pricing
  // (see the comment above DEFAULT_H/DEFAULT_L). Everything below this line
  // that used to reference the module-level H/L/HEADER_FIELDS/etc. now uses
  // these request-scoped equivalents of the same names.
  const { H, L } = useMemo(() => deriveBidKeys(fields), [fields])
  const HEADER_FIELDS = useMemo(() => buildHeaderFields(H), [H])
  const LINE_ALIASES = useMemo(() => buildLineAliases(L), [L])
  const INHERITED_LINE_KEYS = useMemo(() => inheritedLineKeys(L), [L])
  const HEADER_INHERIT_ALIASES = useMemo(() => headerInheritAliases(L), [L])

  // Vendor Bid requests skip RequestDetail's generic top bar (which is where
  // the Extract button normally lives) entirely — this screen is the only
  // place a pending, never-extracted Bid request can trigger AI extraction.
  // Without this, a request whose creation flow didn't auto-start extraction
  // (e.g. auto_analyze not set on ingest) had no way to ever get fields.
  const [extracting, setExtracting] = useState(false)
  const canExtract = request?.status === 'pending' && (request?.documents || []).length > 0
  const handleExtractClick = async () => {
    if (extracting || isReader) return
    setExtracting(true)
    try {
      await requestsApi.analyze(request.id)
      toast.success('Extraction started', 'AI is reading the document — this can take a minute.')
      await onExtract?.()
    } catch (err) {
      toast.error('Failed to start extraction', err?.response?.data?.error || 'Please try again.')
    } finally {
      setExtracting(false)
    }
  }

  // Pull whatever the extraction produced into the Bid's own shape. A field is
  // matched by its ns_field mapping first (the template says where it goes), and
  // only then by name — so a correctly-mapped template needs no guessing.
  const extracted = useMemo(() => {
    const byNsField = {}
    const byName = {}
    for (const f of fields) {
      const val = f.value ?? f.field_value ?? ''
      if (f.ns_field) byNsField[f.ns_field] = val
      if (f.field_name) byName[String(f.field_name).toLowerCase()] = val
    }
    return { byNsField, byName }
  }, [fields])

  // Average extraction confidence across every field that has one — same
  // computation as the PO request page, so the badge means the same thing
  // wherever it's shown.
  const avgConfidence = useMemo(() => {
    const withConf = fields.filter(f => f.confidence != null && f.confidence > 0)
    if (!withConf.length) return null
    return Math.min(100, Math.max(0, Math.round(
      (withConf.reduce((s, f) => s + (f.confidence || 0), 0) / withConf.length) * 100
    )))
  }, [fields])

  // The Bid Line Items table has 9 columns (Item/Qty/List Price/Bid Price/
  // Disc %/Amount/Valid From/Valid To/remove) and was permanently squeezed
  // into a fixed w-1/2 review panel next to the PDF, forcing horizontal
  // scrolling no matter the screen size. Letting the PDF panel collapse
  // gives the table the full width when it's needed, instead of being stuck
  // scrolling a half-width box.
  const [pdfPanelVisible, setPdfPanelVisible] = useState(true)
  const [headerValues, setHeaderValues] = useState({})
  const [supplierId, setSupplierId] = useState('')
  const [supplierName, setSupplierName] = useState('')
  const [resellerId, setResellerId] = useState('')
  const [resellerName, setResellerName] = useState('')
  const [lines, setLines] = useState([])
  const [lookups, setLookups] = useState({ items: [], vendors: [], customers: [] })
  const seeded = useRef(false)

  // Cancel opens the request-cancellation modal.
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  // Seed the form from the extracted document once.
  useEffect(() => {
    if (seeded.current || fields.length === 0) return
    seeded.current = true

    const hv = {}
    for (const f of HEADER_FIELDS) {
      const raw = extracted.byNsField[f.key]
        ?? extracted.byName[f.label.toLowerCase().replace(/\s+/g, '_')]
        ?? ''
      hv[f.key] = f.type === 'date' ? normaliseBidDate(raw) : (raw ?? '')
    }
    // Observed on real Samsung bids: the AI sometimes maps Postal Address onto
    // the same PDF text it already used for End Customer (the two sit next to
    // each other on the document), so the address field silently duplicates
    // the customer's name instead of holding a real address or staying blank.
    // A genuine street address is never identical to the customer name, so
    // this is a safe signal to treat it as unextracted rather than posting a
    // wrong value NetSuite would store as the address.
    const postalRaw = String(hv[H.postalAddress] || '').trim()
    const endCustomerRaw = String(hv[H.endCustomer] || '').trim()
    if (postalRaw && endCustomerRaw && postalRaw === endCustomerRaw) {
      hv[H.postalAddress] = ''
    }
    setHeaderValues(hv)

    // Line items come from the template's array field.
    const tableField = fields.find(f =>
      ['array_of_objects', 'table'].includes((f.field_type || '').toLowerCase()) ||
      f.field_name === 'line_items'
    )
    let parsed = []
    // The API always returns this row's extracted data under field_value —
    // value is null on every field we've seen (confirmed for request 105) —
    // but the header-field seeding above (extracted.byNsField/byName) already
    // falls back to field_value, so it's only this table lookup that reads
    // the always-null key directly and silently drops to a blank line.
    const tableRaw = tableField?.value ?? tableField?.field_value
    if (tableRaw) {
      try {
        const raw = typeof tableRaw === 'string' ? JSON.parse(tableRaw) : tableRaw
        if (Array.isArray(raw)) {
          parsed = raw.map((row, rowIdx) => {
            const norm = {}
            for (const [k, v] of Object.entries(row)) {
              norm[String(k).toLowerCase().trim().replace(/[\s.-]+/g, '_')] = v
            }
            const pick = (nsKey) => {
              for (const alias of (LINE_ALIASES[nsKey] || [])) {
                if (norm[alias] !== undefined && norm[alias] !== '') return norm[alias]
              }
              return ''
            }
            return {
              [L.item]: '',
              _rowIndex: rowIdx,
              _rowText: Object.values(row || {}).filter(v => v !== null && v !== '' && typeof v !== 'object').join(' · '),
              [L.pricingId]: String(pick(L.pricingId) || ''),
              [L.reseller]: String(pick(L.reseller) || ''),
              [L.resellerAddr]: String(pick(L.resellerAddr) || ''),
              _itemText: String(pick(L.item) || ''),
              _description: String(norm.description || norm.item_description || norm.product || ''),
              [L.quantity]: pick(L.quantity),
              [L.listPrice]: pick(L.listPrice),
              [L.bidPrice]: pick(L.bidPrice),
              [L.priceLimit]: pick(L.priceLimit),
              [L.itemNo]: pick(L.itemNo),
              [L.validFrom]: normaliseBidDate(pick(L.validFrom)),
              [L.validTo]: normaliseBidDate(pick(L.validTo)),
            }
          })
        }
      } catch { /* fall through to a single blank line */ }
    }
    setLines(parsed.length > 0 ? parsed : [blankLine(L)])
  }, [fields, extracted])

  const handleCancelRequest = useCallback(async () => {
    if (!cancelReason.trim()) return
    setCancelling(true)
    try {
      await requestsApi.cancel(request.id, cancelReason)
      setShowCancelModal(false)
      navigate(-1)
    } catch {
      toast.error('Failed to cancel request')
    } finally {
      setCancelling(false)
    }
  }, [cancelReason, request.id, navigate, toast])

  // Left-panel tabs: Fields / Activity / AI Insights — same shared chrome as
  // the PO/BC request views.
  const [leftPanelTab, setLeftPanelTab] = useState('fields')
  const [fieldSearchQuery, setFieldSearchQuery] = useState('')
  const [fieldsExtractionFilter, setFieldsExtractionFilter] = useState('all') // 'all', 'extracted', 'not_extracted'
  const [fieldsViewFilter, setFieldsViewFilter] = useState('all') // 'all', 'mandatory' or 'custom'
  const { tabRefs: fieldsViewTabRefs, indicatorStyle: fieldsViewIndicatorStyle } = useSegmentedTabIndicator(fieldsViewFilter, [leftPanelTab])
  const [activityLogs, setActivityLogs] = useState([])
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [activityLoaded, setActivityLoaded] = useState(false)

  const fetchActivityLogs = useCallback(async () => {
    if (!request?.id) return
    try {
      setLoadingActivity(true)
      const res = await requestsApi.getAuditLogs(request.id, { limit: 20 })
      setActivityLogs(formatAuditLogsForActivity(res.data.audit_logs || []))
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setActivityLogs([])
    } finally {
      setLoadingActivity(false)
      setActivityLoaded(true)
    }
  }, [request?.id])

  useEffect(() => {
    if (leftPanelTab === 'activity' && !activityLoaded) {
      fetchActivityLogs()
    }
  }, [leftPanelTab, activityLoaded, fetchActivityLogs])

  // Each Bid header field a person fills in maps 1:1 to an underlying
  // request field (matched by ns_field), which is what actually carries
  // confidence/alternative_count/bounding_box — HEADER_FIELDS itself is just
  // label/type/required metadata, not extraction data.
  const headerFieldsEnriched = useMemo(() => HEADER_FIELDS.map(f => ({
    ...f,
    rf: fields.find(x => x.ns_field === f.key) || null,
  })), [fields])

  const filteredHeaderFields = useMemo(() => {
    let list = headerFieldsEnriched
    if (fieldsViewFilter === 'mandatory') {
      list = list.filter(f => f.required)
    } else if (fieldsViewFilter === 'custom') {
      list = list.filter(f => f.rf?.source_type === 'user_input')
    }
    if (fieldsExtractionFilter === 'extracted') {
      list = list.filter(f => f.rf && ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.rf.source_type))
    } else if (fieldsExtractionFilter === 'not_extracted') {
      list = list.filter(f => !f.rf || !f.rf.source_type || f.rf.source_type === 'pending')
    }
    if (fieldSearchQuery.trim()) {
      const q = fieldSearchQuery.toLowerCase()
      list = list.filter(f => f.label.toLowerCase().includes(q))
    }
    return list
  }, [headerFieldsEnriched, fieldsViewFilter, fieldsExtractionFilter, fieldSearchQuery])

  // Field to highlight in the PDF viewer (bounding_box + page_number), set by
  // clicking a field row — same "click to jump to source" behaviour as the
  // PO request page.
  const [highlightedField, setHighlightedField] = useState(null)

  // Scroll-to + pulse-highlight a field row when a checklist issue referencing
  // it is clicked — same click-to-jump behaviour as the PO/BC checklist cards.
  const fieldRowRefs = useRef({})
  const [pulsedFieldKey, setPulsedFieldKey] = useState(null)
  const fieldJumpCycleRef = useRef({})

  const jumpToFieldKeys = (keys) => {
    const validKeys = (keys || []).filter(Boolean)
    if (validKeys.length === 0) return
    const groupId = validKeys.join('|')
    const idx = (fieldJumpCycleRef.current[groupId] || 0) % validKeys.length
    fieldJumpCycleRef.current[groupId] = idx + 1
    const key = validKeys[idx]
    const el = fieldRowRefs.current[key]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setPulsedFieldKey(key)
    setTimeout(() => setPulsedFieldKey(k => (k === key ? null : k)), 1800)
  }

  const jumpHighlightClass = (key) =>
    pulsedFieldKey === key
      ? 'rounded-lg ring-2 ring-red-400 dark:ring-red-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 animate-pulse'
      : ''

  // Alternatives popup — per-field, keyed by field id so only one is open at a time.
  const [openAlternativesFieldId, setOpenAlternativesFieldId] = useState(null)
  const [alternatives, setAlternatives] = useState([])
  const [loadingAlternatives, setLoadingAlternatives] = useState(false)
  const alternativesPopupRef = useRef(null)

  // Audit history popup — per-field.
  const [openAuditFieldId, setOpenAuditFieldId] = useState(null)
  const [fieldAuditLogs, setFieldAuditLogs] = useState([])
  const [loadingFieldAudit, setLoadingFieldAudit] = useState(false)
  const auditPopupRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alternativesPopupRef.current && !alternativesPopupRef.current.contains(event.target)) {
        setOpenAlternativesFieldId(null)
      }
      if (auditPopupRef.current && !auditPopupRef.current.contains(event.target)) {
        setOpenAuditFieldId(null)
      }
    }
    if (openAlternativesFieldId != null || openAuditFieldId != null) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openAlternativesFieldId, openAuditFieldId])

  // Small "where did this come from" hint shown under the focused field/line.
  const [sourceHint, setSourceHint] = useState(null) // { key, page, text }

  const handleViewFieldInDocument = (field, hintKey) => {
    const hasValue = !!(field?.value ?? '').toString().trim()
    if (!hasValue) {
      setHighlightedField(null)
      setSourceHint(null)
      return
    }
    setSourceHint({
      key: hintKey || field.ns_field || field.field_name,
      page: field.page_number || null,
      text: String(field.value),
    })
    setHighlightedField({
      bounding_box: field.bounding_box,
      page_number: field.page_number,
      field_name: field.field_name,
      extracted_value: field.value,
    })
  }

  const handleToggleAlternatives = async (field) => {
    if (!field) return
    if (openAlternativesFieldId === field.id) {
      setOpenAlternativesFieldId(null)
      return
    }
    setOpenAuditFieldId(null)
    setOpenAlternativesFieldId(field.id)
    setLoadingAlternatives(true)
    try {
      const res = await requestsApi.getFieldAlternatives(request.id, field.field_name)
      setAlternatives(res.data.alternatives || [])
    } catch (err) {
      console.error('Error loading field alternatives:', err)
      setAlternatives([])
    } finally {
      setLoadingAlternatives(false)
    }
  }

  const handleSelectAlternative = (field, alt) => {
    const newValue = alt.field_value || alt.extracted_value || ''
    setHeaderValues(prev => ({ ...prev, [field.ns_field]: newValue }))
    setOpenAlternativesFieldId(null)
    if (alt.bounding_box || alt.page_number) {
      setHighlightedField({
        bounding_box: alt.bounding_box,
        page_number: alt.page_number,
        field_name: field.field_name,
        extracted_value: newValue,
      })
    }
  }

  const handleToggleFieldAudit = async (field) => {
    if (!field) return
    if (openAuditFieldId === field.id) {
      setOpenAuditFieldId(null)
      return
    }
    setOpenAlternativesFieldId(null)
    setOpenAuditFieldId(field.id)
    setLoadingFieldAudit(true)
    try {
      const res = await requestsApi.getFieldAuditLogs(request.id, field.id, { limit: 20 })
      setFieldAuditLogs(res.data.audit_logs || [])
    } catch (err) {
      console.error('Error loading field audit log:', err)
      setFieldAuditLogs([])
    } finally {
      setLoadingFieldAudit(false)
    }
  }

  // One initialisation call for the dropdown data (includes customers for Bid).
  useEffect(() => {
    let cancelled = false
    nsApi.getLookups(request?.template_id)
      .then(res => { if (!cancelled) setLookups(res.data || { items: [], vendors: [], customers: [] }) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [request?.template_id])


  // Auto-resolve extracted item codes to NS internal ids, the same way the PO
  // view does — so a document whose codes match NetSuite needs no manual picking.
  const resolvedOnce = useRef(false)
  useEffect(() => {
    if (resolvedOnce.current || lines.length === 0) return
    const codes = lines.map(l => l._itemText).filter(Boolean)
    if (codes.length === 0) return
    resolvedOnce.current = true
    // `lines` lets the backend also match the manufacturer part number
    // (vendorname/mpn) and model codes in the description; `by_line[i]`
    // answers line i. Falls back to the plain itemid map.
    const queried = lines.map(l => ({ code: l._itemText || '', description: l._description || '' }))
    nsApi.resolveItems(codes, undefined, queried)
      .then(res => {
        const map = res.data?.resolved || {}
        const byLine = Array.isArray(res.data?.by_line) ? res.data.by_line : null
        setLines(prev => prev.map((l, i) => {
          const hit = (byLine && i < queried.length ? byLine[i] : null)
            || map[String(l._itemText || '').toLowerCase()]
          return hit ? {
            // Swap the box's label to the resolved NS item code (hit.number) —
            // same as a manual pick's onChange. Left as the raw extracted text
            // (e.g. a Samsung manufacturer part number like LH115QHFEBGXXY)
            // before this, the box looked like it had matched the wrong item
            // even when resolution and posting were both correct.
            ...l, [L.item]: hit.id, _itemText: hit.number || l._itemText,
            _description: l._description || hit.description,
            _matchSource: hit.match_source || '', _sourceCode: l._itemText,
          } : l
        }))
      })
      .catch(() => {})
  }, [lines.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // The line-items table field — its bounding box/page is the best source a
  // line cell has, since extraction locates the table, not each cell.
  const tableFieldForSource = useMemo(() => fields.find(f =>
    ['array_of_objects', 'table'].includes((f.field_type || '').toLowerCase()) ||
    f.field_name === 'line_items'
  ) || null, [fields])

  const handleLineFocus = (idx) => {
    const line = lines[idx]
    if (!line) return
    const tf = tableFieldForSource
    if (tf && (tf.bounding_box || tf.page_number)) {
      setHighlightedField({
        bounding_box: tf.bounding_box,
        page_number: tf.page_number,
        field_name: tf.field_name,
        extracted_value: line._rowText || '',
      })
    }
    setSourceHint({
      key: `line:${idx}`,
      page: tf?.page_number || null,
      text: line._rowText
        ? `Row ${(line._rowIndex ?? idx) + 1}: ${line._rowText}`
        : 'Added by hand — not from the document',
    })
  }

  // Name posted on the header and every line: the uploaded file's name without
  // its extension. Prefer the backend's value when it sends one.
  const [serverRecordName, setServerRecordName] = useState('')
  const [supplierDefault, setSupplierDefault] = useState(null)
  useEffect(() => {
    if (!request?.id || !nsApi.getBidRecordName) return
    let cancelled = false
    nsApi.getBidRecordName(request.id, request.organization_id)
      .then(res => {
        if (cancelled) return
        setServerRecordName(res.data?.bid_record_name || '')
        setSupplierDefault(res.data?.supplier_default?.id ? res.data.supplier_default : null)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [request?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const recordName = useMemo(() => {
    const fromBackend = serverRecordName || request?.ns_record_name || request?.source_name || request?.document_name
    if (fromBackend) return String(fromBackend)
    const docs = request?.documents || []
    const pdf = docs.find(d => d.content_type === 'application/pdf' || d.filename?.endsWith('.pdf'))
    const fn = pdf?.filename || docs[0]?.filename || ''
    return fn.replace(/\.[^./\\]+$/, '')
  }, [request, serverRecordName])

  // Header values every line inherits when it has none of its own.
  const headerInherit = useMemo(() => {
    const pick = (key) => {
      if (extracted.byNsField[key]) return extracted.byNsField[key]
      for (const a of HEADER_INHERIT_ALIASES[key] || []) {
        if (extracted.byName[a]) return extracted.byName[a]
      }
      return ''
    }
    return {
      [L.pricingId]: String(pick(L.pricingId) || ''),
      [L.validFrom]: normaliseBidDate(pick(L.validFrom)) || '',
      [L.validTo]: normaliseBidDate(pick(L.validTo)) || '',
      [L.resellerAddr]: String(pick(L.resellerAddr) || ''),
      // Displayed by name, posted by internal id (see linePayload).
      [L.reseller]: resellerName || '',
    }
  }, [extracted, resellerName])

  // Reseller names the reviewer confirmed by hand this session — sent with the
  // post so the backend can learn them even if the confirm call didn't land.
  const [confirmedNames, setConfirmedNames] = useState([])
  const handleResellerResolved = (id, name) => {
    setResellerId(id || ''); setResellerName(name || '')
  }
  // AI-mapped bid templates store Reseller under the LINE key (it is posted
  // per line), so both keys are read. Neither byNsField nor byName can ever
  // find it when Reseller exists only as a column inside the line_items
  // table (no top-level field carries that ns_field/label) — the printed
  // value is always inside the extracted line rows in that case, so fall
  // back to the first parsed line, which is where header-level inheritance
  // copies it from anyway (INHERITED_LINE_KEYS below).
  const extractedResellerName = String(
    extracted.byNsField[H.reseller] || extracted.byNsField[L.reseller]
    || extracted.byName.reseller || lines[0]?.[L.reseller] || ''
  ).trim()
  const extractedEndCustomerName = String(
    extracted.byNsField[H.endCustomer] || extracted.byName.end_customer || extracted.byName['end customer'] || ''
  ).trim()
  // Only an explicit reviewer pick (candidate click or a search they ran) is
  // learned — CustomerField's on-mount fuzzy auto-match never is, whatever
  // mode the view mounted in.
  const learnCustomerPick = (extractedName, id, name) => {
    if (!id || !extractedName) return
    if (extractedName.toLowerCase() === String(name || '').trim().toLowerCase()) return
    const entry = { entity_type: 'customer', extracted_name: extractedName, ns_customer_id: id, ns_customer_name: name || '' }
    setConfirmedNames(prev => [...prev.filter(c => c.extracted_name !== extractedName), entry])
    if (typeof nsApi.confirmCustomer === 'function') {
      Promise.resolve(nsApi.confirmCustomer({ extracted_name: extractedName, ns_customer_id: id, ns_customer_name: name || '' }))
        .then(() => toast.success('Saved to name mappings', 'Next time this matches automatically.'))
        .catch(err => {
          // 403: a plain reviewer may not repoint a name an admin already mapped.
          if (err?.response?.status === 403) toast.error(err.response.data?.error || 'Name mapping not changed')
        })
    }
  }

  const [endCustomerId, setEndCustomerId] = useState('')
  const handleEndCustomerResolved = (id, name) => {
    setEndCustomerId(id || '')
    if (name) setHeaderValues(v => ({ ...v, [H.endCustomer]: name }))
  }

  // ── Payload projection (single source of truth for validate AND post) ──
  const headerPayload = useMemo(() => {
    const p = {}
    for (const f of HEADER_FIELDS) {
      const v = headerValues[f.key]
      if (v !== undefined && v !== null && String(v).trim() !== '') p[f.key] = v
    }
    if (supplierId) p[H.supplier] = supplierId
    if (resellerId) p[H.reseller] = resellerId
    // Posted by internal id when picked; the backend shapes it for the live
    // field type (reference -> {id}, text field -> the customer's name).
    if (endCustomerId) p[H.endCustomer] = endCustomerId
    return p
  }, [headerValues, supplierId, resellerId, endCustomerId])

  const linePayload = useMemo(() => lines
    .filter(l => l[L.item] || l._itemText || l[L.bidPrice])
    .map(l => {
      const p = {
        [L.item]: l[L.item] || '',
        [L.quantity]: toNumber(l[L.quantity]),
        [L.listPrice]: toNumber(l[L.listPrice]),
        [L.bidPrice]: toNumber(l[L.bidPrice]),
        [L.validFrom]: l[L.validFrom] || headerInherit[L.validFrom] || '',
        [L.validTo]: l[L.validTo] || headerInherit[L.validTo] || '',
      }
      const priceLimit = toNumber(l[L.priceLimit])
      if (priceLimit !== null) p[L.priceLimit] = priceLimit
      const itemNo = toNumber(l[L.itemNo])
      if (itemNo !== null) p[L.itemNo] = itemNo
      const pricingId = l[L.pricingId] || headerInherit[L.pricingId]
      if (pricingId) p[L.pricingId] = pricingId
      const addr = l[L.resellerAddr] || headerInherit[L.resellerAddr]
      if (addr) p[L.resellerAddr] = addr
      // resellerId (the header picker's confirmed/resolved NetSuite id) must
      // win once it's set — l[L.reseller] only ever holds the RAW extracted
      // text (added so the header box could see it; see extractedResellerName
      // above), and that text is never itself a valid NetSuite id. Checking
      // it first meant a confirmed header selection was silently discarded at
      // post time and the unresolved raw text got sent instead, which is
      // exactly what NetSuite's "not a resolved NetSuite reseller" rejection
      // was reporting — the user's pick was never wrong, it just never
      // reached the payload.
      const reseller = resellerId || l[L.reseller]
      if (reseller) p[L.reseller] = reseller
      if (recordName) p.name = recordName
      return p
    }), [lines, headerInherit, resellerId, recordName])

  // ── Local checklist (mirrors what the server will enforce) — grouped for the
  // unified checklist card, same shape as NSRequestView's PO checklist. ──
  const issues = useMemo(() => {
    const out = []
    if (!supplierId) out.push({ group: 'Supplier', label: 'Supplier', detail: 'Not confirmed yet', keys: ['supplier'] })
    for (const f of HEADER_FIELDS) {
      if (f.required && !String(headerValues[f.key] ?? '').trim()) {
        out.push({ group: 'Bid Details', label: f.label, detail: 'Required — not filled in', keys: [f.key] })
      }
    }
    if (linePayload.length === 0) out.push({ group: 'Line Items', label: 'Line items', detail: 'No line items added', keys: ['lines'] })
    lines.forEach((l, i) => {
      const n = i + 1
      if (!l[L.item]) out.push({ group: 'Line Items', label: `Line ${n} — Item`, detail: 'Not matched to a NetSuite item', keys: ['lines'] })
      if (toNumber(l[L.quantity]) === null || toNumber(l[L.quantity]) <= 0) out.push({ group: 'Line Items', label: `Line ${n} — Quantity`, detail: 'Must be greater than 0', keys: ['lines'] })
      if (toNumber(l[L.listPrice]) === null) out.push({ group: 'Line Items', label: `Line ${n} — List Price`, detail: 'Required', keys: ['lines'] })
      if (toNumber(l[L.bidPrice]) === null) out.push({ group: 'Line Items', label: `Line ${n} — Bid Price`, detail: 'Required', keys: ['lines'] })
      const vFrom = l[L.validFrom] || headerInherit[L.validFrom]
      const vTo = l[L.validTo] || headerInherit[L.validTo]
      if (!vFrom) out.push({ group: 'Line Items', label: `Line ${n} — Valid From`, detail: 'Required', keys: ['lines'] })
      if (!vTo) out.push({ group: 'Line Items', label: `Line ${n} — Valid To`, detail: 'Required', keys: ['lines'] })
      if (vFrom && vTo && vFrom > vTo) {
        out.push({ group: 'Line Items', label: `Line ${n} — Valid To`, detail: 'Must be on or after Valid From', keys: ['lines'] })
      }
    })
    return out
  }, [supplierId, headerValues, lines, linePayload.length, headerInherit])

  const issueGroups = useMemo(() => {
    const order = ['Supplier', 'Bid Details', 'Line Items']
    return order
      .map(group => ({ group, items: issues.filter(i => i.group === group) }))
      .filter(g => g.items.length > 0)
  }, [issues])

  const [showChecklist, setShowChecklist] = useState(false)

  const canPost = issues.length === 0 && !isReader

  // ── Posting ──
  const [posting, setPosting] = useState(false)
  const [postMinimized, setPostMinimized] = useState(false)
  const [postProgress, setPostProgress] = useState({ percent: 0, message: '', lineAt: 0, lineTotal: 0 })
  const [postResult, setPostResult] = useState(() =>
    request?.ns_post_status === 'posted' || request?.ns_po_number
      ? { success: true, bid_number: request.ns_po_number, ns_url: request.ns_url }
      : null
  )
  const [serverErrors, setServerErrors] = useState([])
  const [postAttempts, setPostAttempts] = useState(request?.ns_post_attempts || 0)
  const MAX_ATTEMPTS = 3

  async function handlePost() {
    if (posting) return
    setPosting(true); setPostResult(null); setServerErrors([]); setPostMinimized(false)
    setPostProgress({ percent: 5, message: 'Preparing fields…', lineAt: 0, lineTotal: linePayload.length })
    try {
      setPostProgress({ percent: 25, message: 'Validating against NetSuite…', lineAt: 0, lineTotal: linePayload.length })
      const v = await nsApi.validate({
        request_id: request.id, org_id: orgId,
        payload: headerPayload, line_items: linePayload,
        ns_entity_type: 'vendor_bid',
      })
      if (!v.data?.valid) {
        setServerErrors(v.data?.errors || [])
        setPosting(false); setPostProgress({ percent: 0, message: '', lineAt: 0, lineTotal: 0 })
        return
      }
      setPostProgress({ percent: 50, message: 'Creating bid in NetSuite…', lineAt: 0, lineTotal: linePayload.length })
      const res = await nsApi.createVendorBid({
        request_id: request.id, org_id: orgId,
        header_fields: headerPayload, line_items: linePayload,
        ...(confirmedNames.length ? { confirmed_names: confirmedNames } : {}),
      })
      const d = res.data || {}
      setPostProgress({ percent: 90, message: 'Finalising…', lineAt: d.lines_created || 0, lineTotal: d.lines_total || linePayload.length })
      await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
      setPostProgress({ percent: 100, message: 'Special Pricing created!', lineAt: d.lines_created || 0, lineTotal: d.lines_total || 0 })
      setTimeout(() => {
        setPosting(false)
        setPostResult({ success: true, bid_number: d.bid_number, ns_url: d.ns_url, lines_created: d.lines_created, lines_total: d.lines_total })
        setPostAttempts(0)
      }, 700)
      toast.success('Posted to NetSuite', `Special Pricing ${d.bid_number || ''} created with ${d.lines_created} line items.`)
    } catch (err) {
      const d = err?.response?.data || {}
      const attempt = postAttempts + 1; setPostAttempts(attempt)
      setPosting(false); setPostProgress({ percent: 0, message: '', lineAt: 0, lineTotal: 0 })
      // A partial bid is its own state: the header and some lines DID land, and
      // a retry completes only the rest. Reported as calmly as a full failure.
      setPostResult({
        success: false, partial: !!d.partial, error: d.error || 'Request failed',
        ns_url: d.ns_url, ns_internal_id: d.ns_internal_id,
        lines_created: d.lines_created, lines_total: d.lines_total,
        line_errors: d.line_errors || [], attempt,
      })
    }
  }

  async function handleRetry() {
    if (posting || postAttempts >= MAX_ATTEMPTS) return
    setPosting(true)
    setPostProgress({ percent: 40, message: 'Completing the missing line items…', lineAt: postResult?.lines_created || 0, lineTotal: postResult?.lines_total || 0 })
    try {
      const res = await nsApi.retryVendorBid({ request_id: request.id, org_id: orgId })
      const d = res.data || {}
      setPostResult({ success: true, bid_number: d.bid_number, ns_url: d.ns_url, lines_created: d.lines_created, lines_total: d.lines_total })
      setPostAttempts(0)
      await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
      toast.success('Retry succeeded', `Special Pricing completed — ${d.lines_created} of ${d.lines_total} line items.`)
    } catch (err) {
      const d = err?.response?.data || {}
      const attempt = d.attempt_number || postAttempts + 1
      setPostAttempts(attempt)
      setPostResult({
        success: false, partial: !!d.partial, error: d.error || 'Retry failed',
        ns_url: d.ns_url, lines_created: d.lines_created, lines_total: d.lines_total,
        line_errors: d.line_errors || [], attempt,
      })
    } finally { setPosting(false) }
  }

  // request.documents entries never carry a download_url/document_url — the
  // backend doesn't serialize one (confirmed: no such key anywhere in
  // requests_api.py). PDFViewer doesn't even accept a fileUrl prop; it takes
  // documentId and fetches its own SAS URL internally (documentsApi.getSasUrl).
  // Mirrors NSRequestView's pdfDocId — the PO sibling screen already does this
  // correctly.
  const pdfDoc = (request?.documents || []).find(
    d => d.content_type === 'application/pdf' || d.filename?.endsWith('.pdf')
  )
  const pdfDocId = pdfDoc?.id

  // Collapse the header's title/badge row to reclaim vertical space, same
  // affordance as the normal extraction and PO request pages.
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)

  // Draggable split between Fields panel (left) and document viewer (right),
  // matching the PO/BC/normal extraction page's fields-left/document-right layout.
  const [leftPanelPct, setLeftPanelPct] = useState(50)
  const [isDraggingDivider, setIsDraggingDivider] = useState(false)
  const mainContentRef = useRef(null)

  const handleDividerMouseDown = (e) => {
    e.preventDefault()
    setIsDraggingDivider(true)
    const onMouseMove = (moveEvent) => {
      if (!mainContentRef.current) return
      const rect = mainContentRef.current.getBoundingClientRect()
      let pct = ((moveEvent.clientX - rect.left) / rect.width) * 100
      pct = Math.min(80, Math.max(20, pct))
      setLeftPanelPct(pct)
    }
    const onMouseUp = () => {
      setIsDraggingDivider(false)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  return (
    <>
    {/* AI Processing overlay — same as NSRequestView (PO) and the normal extraction page */}
    {showProcessingModal && (
      processingMinimized ? (
        <button onClick={() => setProcessingMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Processing...</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{processingProgress.percent}% — click to expand</p>
          </div>
          <Maximize2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 ml-1" />
        </button>
      ) : (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={() => setProcessingMinimized(true)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-lg w-full mx-4">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 opacity-10 animate-pulse" />
            <div className="relative flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                  <Bot className="h-8 w-8 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">AI is Extracting Details</h3>
              <div className="w-full mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{processingProgress.message || 'Starting...'}</span>
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{processingProgress.percent}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(processingProgress.percent, 2)}%` }} />
                </div>
              </div>
              <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto text-left">
                {processingLog.length === 0 ? (
                  <div className="p-3 flex items-center gap-2 text-xs text-gray-400"><Loader2 className="h-3 w-3 animate-spin" /><span>Waiting…</span></div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {processingLog.map((step, idx) => (
                      <div key={idx} className="px-3 py-1.5 flex items-start gap-2">
                        <span className="text-[10px] font-mono text-gray-400 whitespace-nowrap mt-0.5">{step.elapsed_seconds != null ? `${step.elapsed_seconds}s` : step.timestamp}</span>
                        <span className={`text-xs ${step.message?.includes('✅') ? 'text-green-600' : step.message?.includes('❌') ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{step.message}</span>
                      </div>
                    ))}
                    <div ref={el => el?.scrollIntoView({ behavior: 'smooth', block: 'end' })} />
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Click anywhere outside to minimize</p>
            </div>
          </div>
        </div>
      )
    )}

      <div className="-m-6 flex flex-col bg-gray-100 dark:bg-gray-950 overflow-hidden animate-page-in" style={{ height: 'calc(100vh - 4rem)' }}>

        <UnassignedOpCoBanner request={request} onMoved={onOpCoMoved} />
        {/* Top header bar — same gradient/dot-pattern + icon-badge + title design as the PO/BC/normal extraction page */}
        <div className="relative bg-gradient-to-r from-navy-100/90 via-navy-50/70 to-navy-100/80 dark:from-gray-900/80 dark:via-gray-800/60 dark:to-gray-900/70 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 animate-header-in">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, var(--tw-color-navy-900, #0f2942) 1px, transparent 0)',
              backgroundSize: '20px 20px',
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-navy-600 dark:bg-navy-500"
          />
          <div
            className={`relative z-10 pl-6 pr-5 flex items-start justify-between gap-4 flex-wrap transition-[padding] duration-300 ease-out ${
              isHeaderCollapsed ? 'py-2.5' : 'pt-4 pb-3'
            }`}
          >
            {/* LEFT: icon badge + title + type/file chips — collapsible */}
            <div className={`relative flex min-w-0 flex-1 basis-[280px] ${isHeaderCollapsed ? 'items-center gap-3' : 'items-start gap-3.5'}`}>
              <div
                className={`hidden sm:flex flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-600 to-navy-800 text-white shadow-md ring-1 ring-navy-900/10 transition-all ${
                  isHeaderCollapsed ? 'h-9 w-9' : 'h-11 w-11'
                }`}
                title="NetSuite Special Pricing request"
              >
                <FileText className={isHeaderCollapsed ? 'h-4 w-4' : 'h-5 w-5'} />
              </div>
              <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                  <h1
                    className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-snug truncate"
                    title="Request title"
                  >
                    {request?.title || `Request #${request?.id}`}
                  </h1>
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300 flex-shrink-0"
                    title="NetSuite record type this request will post as"
                  >
                    Special Pricing
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsHeaderCollapsed(prev => !prev)}
                    title={isHeaderCollapsed ? 'Expand details' : 'Collapse details'}
                    className="flex-shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-navy-600 dark:hover:text-navy-400 hover:bg-navy-50 dark:hover:bg-navy-900/20 transition-colors"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${isHeaderCollapsed ? '-rotate-90' : ''}`} />
                  </button>
                  <OpCoBadge request={request} />
                  <MoveOpCoButton request={request} onMoved={onOpCoMoved} />
                </div>
                {!isHeaderCollapsed && (request?.template_name || request?.template?.name || pdfDoc?.filename) && (
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    {(request?.template_name || request?.template?.name) && (
                      <span
                        className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                        title="Extraction template used for this request"
                      >
                        <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                          <FileSpreadsheet className="h-2.5 w-2.5" />
                        </span>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                          {request?.template_name || request?.template?.name}
                        </span>
                      </span>
                    )}
                    {pdfDoc?.filename && (
                      <span
                        className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                        title="Source document attached to this request"
                      >
                        <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                          <FileText className="h-2.5 w-2.5" />
                        </span>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                          {pdfDoc.filename}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: AI confidence / status (top row) + action buttons (below) */}
            <div className={`flex flex-shrink-0 gap-2 ${isHeaderCollapsed ? 'flex-row items-center' : 'flex-col items-end'}`}>
              {!isHeaderCollapsed && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 shrink min-w-0 whitespace-nowrap">
                  {avgConfidence != null && (
                    <span className={`group relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 cursor-default ${
                      avgConfidence >= 90
                        ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                        : avgConfidence >= 70
                        ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400'
                        : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      AI Confidence: <strong>{avgConfidence}%</strong>

                      {/* Confidence legend — shown on hover, same as the PO request page */}
                      <div className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                        <div className="flex items-center gap-2.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-gray-500 dark:text-gray-400 font-normal">&gt;90%</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                            <span className="text-gray-500 dark:text-gray-400 font-normal">70-89%</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-gray-500 dark:text-gray-400 font-normal">&lt;70%</span>
                          </div>
                        </div>
                      </div>
                    </span>
                  )}
                  {/* Saved timestamp — informational, stays above the action buttons */}
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500 flex-shrink-0">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="text-xs whitespace-nowrap">
                      Saved {request?.updated_at ? formatTimeAgo(request.updated_at) : 'N/A'}
                    </span>
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2.5 flex-wrap justify-end">
                {canExtract && (
                  <button
                    onClick={handleExtractClick}
                    disabled={extracting || isReader}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white text-xs font-semibold bg-navy-700 hover:bg-navy-800 ring-1 ring-navy-900/10 shadow-sm shadow-navy-900/20 hover:shadow-lg hover:shadow-navy-900/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 active:scale-[0.96] disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isReader ? 'Read-only access' : 'Start AI extraction from the attached document'}
                  >
                    {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
                    {extracting ? 'Starting…' : 'Extract'}
                  </button>
                )}
                {request?.can_cancel !== false && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    disabled={isReader}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold text-red-600 dark:text-red-300 bg-white dark:bg-gray-900 border border-red-300 dark:border-red-400/40 hover:bg-red-50 hover:border-red-400 dark:hover:bg-red-400/10 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.96] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div ref={mainContentRef} className={`flex flex-1 overflow-hidden ${isDraggingDivider ? 'select-none cursor-col-resize' : ''}`}>

          {/* LEFT: Fields — fields on the left, document on the right, matching the PO/BC/normal extraction page */}
          <section
            style={{ width: pdfPanelVisible ? `${leftPanelPct}%` : '100%' }}
            className={`min-w-[320px] flex-shrink-0 flex flex-col bg-white dark:bg-gray-900 overflow-hidden ${isDraggingDivider ? '' : 'transition-all duration-150'}`}
          >
            <div className="px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/60 dark:to-gray-900 pt-3.5 pb-3.5">
              <RequestPanelTabs
                tabs={[
                  { key: 'fields', label: 'Fields', icon: ClipboardList },
                  { key: 'activity', label: 'Activity', icon: Activity },
                  { key: 'ai_ops', label: 'AI Insights', icon: Sparkles },
                ]}
                activeTab={leftPanelTab}
                onTabChange={setLeftPanelTab}
                isDocViewerVisible={pdfPanelVisible}
                onToggleDocViewer={() => { setPdfPanelVisible(v => !v); setLeftPanelPct(50) }}
              />
              {leftPanelTab === 'fields' && (
                <FieldsPanelHeader
                  fieldCount={headerFieldsEnriched.length}
                  searchQuery={fieldSearchQuery}
                  onSearchChange={setFieldSearchQuery}
                  extractionFilter={fieldsExtractionFilter}
                  onExtractionFilterChange={setFieldsExtractionFilter}
                  extractedCount={
                    (fieldsViewFilter === 'mandatory'
                      ? headerFieldsEnriched.filter(f => f.required)
                      : fieldsViewFilter === 'custom'
                        ? headerFieldsEnriched.filter(f => f.rf?.source_type === 'user_input')
                        : headerFieldsEnriched
                    ).filter(f => f.rf && ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.rf.source_type)).length
                  }
                  notExtractedCount={
                    (fieldsViewFilter === 'mandatory'
                      ? headerFieldsEnriched.filter(f => f.required)
                      : fieldsViewFilter === 'custom'
                        ? headerFieldsEnriched.filter(f => f.rf?.source_type === 'user_input')
                        : headerFieldsEnriched
                    ).filter(f => !f.rf || !f.rf.source_type || f.rf.source_type === 'pending').length
                  }
                  viewFilter={fieldsViewFilter}
                  onViewFilterChange={setFieldsViewFilter}
                  viewIndicatorStyle={fieldsViewIndicatorStyle}
                  viewTabRefs={fieldsViewTabRefs}
                />
              )}
            </div>

            {leftPanelTab === 'activity' && (
              <ActivityLogPanel logs={activityLogs} loading={loadingActivity} onRefresh={fetchActivityLogs} />
            )}

            {leftPanelTab === 'ai_ops' && (
              <div className="flex-1 overflow-y-auto px-4 py-4 animate-panel-in">
                <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 mb-3">AI Insights</h3>
                <CostBreakdown requestId={request.id} embedded />
              </div>
            )}

            {leftPanelTab === 'fields' && (
            <div className="flex-1 overflow-y-auto p-5 space-y-5 animate-panel-in">

            {/* ── Bid Details (header record) ── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Bid Details</h3>
              <div className="space-y-5">
                {recordName && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name <span className="font-normal text-gray-400">(from filename)</span>
                    </label>
                    <div className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300 truncate"
                      title={`${recordName} — used as the name on the bid and every line`}>
                      {recordName}
                    </div>
                  </div>
                )}
                {(() => {
                  const supplierExtractedValue = extracted.byNsField[H.supplier] || extracted.byName.supplier || extracted.byName.vendor || ''
                  const supplierRf = fields.find(x => x.ns_field === H.supplier)
                  const sConfidence = supplierRf?.confidence || 0
                  const sConfidencePercent = Math.round(sConfidence * 100)
                  const sIsHighConf = sConfidence >= 0.9
                  const sIsMedConf = sConfidence >= 0.7 && sConfidence < 0.9
                  const sHasValue = !!(supplierRf?.value ?? '').toString().trim()
                  return (
                    <div
                      ref={el => { fieldRowRefs.current['supplier'] = el }}
                      className={`p-1 -m-1 transition-shadow ${jumpHighlightClass('supplier')}`}
                    >
                      <label
                        onClick={() => sHasValue && handleViewFieldInDocument(supplierRf)}
                        className={`flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 ${sHasValue ? 'cursor-pointer w-fit' : ''}`}
                      >
                        {showColorCoding && sHasValue && sConfidence > 0 && (
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sIsHighConf ? 'bg-green-500' : sIsMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                        )}
                        Supplier <span className="text-red-500">*</span>
                        {showConfidenceScore && sHasValue && sConfidence > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            sIsHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : sIsMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>{sConfidencePercent}%</span>
                        )}
                      </label>
                      <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                        <VendorField
                          extractedValue={supplierExtractedValue}
                          vendors={lookups.vendors || []}
                          requestId={request?.id}
                          orgId={orgId}
                          templateId={request?.template_id}
                          defaultVendor={supplierDefault}
                          onVendorResolved={(id, name) => { setSupplierId(id || ''); setSupplierName(name || '') }}
                          onVendorCleared={() => { setSupplierId(''); setSupplierName('') }}
                        />
                      </EditGate>
                    </div>
                  )
                })()}

                <div
                  ref={el => { fieldRowRefs.current['reseller'] = el }}
                  className={`p-1 -m-1 transition-shadow ${jumpHighlightClass('reseller')}`}
                >
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Reseller</label>
                  <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                    <CustomerField
                      // Reseller only exists inside the line_items table, so
                      // its value arrives one render after this component
                      // first mounts (the table-parsing effect runs, then
                      // `lines` updates, then extractedResellerName recomputes).
                      // CustomerField resolves its extractedValue exactly
                      // once, on mount, and never retries — without this key,
                      // it locks in on the empty string from the first render
                      // and the real value that arrives moments later is
                      // never looked up. Keying on the value forces a fresh
                      // mount once the real text is available.
                      key={extractedResellerName}
                      extractedValue={extractedResellerName}
                      customers={lookups.customers || []}
                      orgId={orgId}
                      onCustomerResolved={handleResellerResolved}
                      onCustomerPicked={(id, name) => learnCustomerPick(extractedResellerName, id, name)}
                      onCustomerCleared={() => { setResellerId(''); setResellerName('') }}
                    />
                  </EditGate>
                </div>

                {filteredHeaderFields.map(f => {
                  const rf = f.rf
                  const val = headerValues[f.key] ?? ''
                  const isEmpty = !String(val).trim()
                  const confidence = rf?.confidence || 0
                  const confidencePercent = Math.round(confidence * 100)
                  const isHighConf = confidence >= 0.9
                  const isMedConf = confidence >= 0.7 && confidence < 0.9
                  const alternativeCount = rf?.alternative_count || 0
                  const hasAlternatives = alternativeCount >= 1
                  const isAlternativesOpen = !!rf && openAlternativesFieldId === rf.id
                  const isAuditOpen = !!rf && openAuditFieldId === rf.id
                  return (
                    <div
                      key={f.key}
                      ref={el => { fieldRowRefs.current[f.key] = el }}
                      onClick={() => rf && handleViewFieldInDocument(rf, f.key)}
                      onFocusCapture={() => rf && handleViewFieldInDocument(rf, f.key)}
                      className={`relative space-y-1 p-1 -m-1 transition-shadow ${rf ? 'cursor-pointer' : ''} ${jumpHighlightClass(f.key)}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 min-w-0">
                          {showColorCoding && !isEmpty && confidence > 0 && (
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHighConf ? 'bg-green-500' : isMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                          )}
                          <span className="truncate">{f.label}</span>
                          {f.required && <span className="text-red-500">*</span>}
                          {showConfidenceScore && !isEmpty && confidence > 0 && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${isHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : isMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{confidencePercent}%</span>
                          )}
                        </label>
                        {rf && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleToggleFieldAudit(rf) }}
                              className={`flex-shrink-0 p-1 rounded-md transition-all ${isAuditOpen ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300'}`}
                              title="View edit history"
                            >
                              <History className="h-3.5 w-3.5" strokeWidth={isAuditOpen ? 2.75 : 2} />
                            </button>
                            {hasAlternatives && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleToggleAlternatives(rf) }}
                                className={`flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-all border ${isAlternativesOpen ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                                title={`${alternativeCount} alternative value(s) from different documents`}
                              >
                                <Layers className="h-3 w-3" />
                                <span>{alternativeCount + 1}</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {f.key === H.endCustomer ? (
                        <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                          <CustomerField
                            // Same remount trick as Reseller above: End Customer's
                            // extracted value can arrive a render after this field
                            // first mounts, and CustomerField only reads its
                            // extractedValue prop once on mount. Without this key,
                            // a value that lands late is silently dropped.
                            key={extractedEndCustomerName}
                            extractedValue={extractedEndCustomerName || String(val || '')}
                            customers={lookups.customers || []}
                            orgId={orgId}
                            onCustomerResolved={handleEndCustomerResolved}
                            onCustomerPicked={(id, name) => learnCustomerPick(extractedEndCustomerName, id, name)}
                            onCustomerCleared={() => setEndCustomerId('')}
                          />
                        </EditGate>
                      ) : (
                      <input
                        type={f.type}
                        value={val}
                        readOnly
                        onClick={e => e.stopPropagation()}
                        className={`w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 transition-colors duration-200 ${
                          f.required && isEmpty
                            ? 'border-amber-400 dark:border-amber-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      )}
                      {sourceHint?.key === f.key && <SourceHint hint={sourceHint} />}

                      {/* Alternatives popup */}
                      {isAlternativesOpen && rf && (
                        <div
                          ref={alternativesPopupRef}
                          onClick={e => e.stopPropagation()}
                          className="absolute z-50 top-full right-0 mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                          style={{ width: '320px' }}
                        >
                          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              Select value <span className="text-gray-400">({alternatives.length + 1} found)</span>
                            </span>
                            <button onClick={() => setOpenAlternativesFieldId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {loadingAlternatives ? (
                            <div className="p-6 text-center">
                              <RefreshCw className="h-4 w-4 animate-spin mx-auto text-blue-500" />
                              <span className="text-xs text-gray-500 mt-2 block">Loading...</span>
                            </div>
                          ) : (
                            <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                              {[{ id: rf.id, field_value: rf.value, confidence: rf.confidence, source_document_filename: rf.source_document_filename, page_number: rf.page_number, isActive: true }, ...alternatives.map(a => ({ ...a, isActive: false }))].map(item => (
                                <div
                                  key={item.id}
                                  className={`px-3 py-2.5 transition-all cursor-pointer flex items-center gap-3 ${item.isActive ? 'bg-green-50 dark:bg-green-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                  onClick={() => !item.isActive && handleSelectAlternative(rf, item)}
                                >
                                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${item.isActive ? 'border-green-500 bg-green-500' : 'border-gray-300 hover:border-blue-500'}`}>
                                    {item.isActive && <Check className="h-2.5 w-2.5 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm truncate ${item.isActive ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                      {item.field_value || item.extracted_value || '(empty)'}
                                    </p>
                                    <p className="text-xs text-gray-400 truncate mt-0.5">
                                      {item.source_document_filename || 'Unknown source'}
                                      {item.page_number && <span className="ml-1">• p.{item.page_number}</span>}
                                    </p>
                                  </div>
                                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${(item.confidence || 0) >= 0.9 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : (item.confidence || 0) >= 0.7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                                    {Math.round((item.confidence || 0) * 100)}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Audit history popup */}
                      {isAuditOpen && rf && (
                        <AnchoredPopover
                          ref={auditPopupRef}
                          onClick={e => e.stopPropagation()}
                          anchorClassName="top-full right-0"
                          className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
                          style={{ width: '340px' }}
                        >
                          <div className="px-3.5 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 flex items-center justify-between">
                            <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                              <History className="h-3.5 w-3.5" /> Edit History
                            </span>
                            <button onClick={() => setOpenAuditFieldId(null)} className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-md transition-colors">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {loadingFieldAudit ? (
                            <div className="p-6 text-center">
                              <Loader2 className="h-4 w-4 animate-spin mx-auto text-navy-500" />
                              <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">Loading history...</span>
                            </div>
                          ) : fieldAuditLogs.length === 0 ? (
                            <div className="p-6 text-center">
                              <History className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-1.5" />
                              <p className="text-xs text-gray-500 dark:text-gray-400">No edit history found</p>
                            </div>
                          ) : (
                            <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                              {fieldAuditLogs.map((log, idx) => {
                                let oldValue = null, newValue = null
                                try {
                                  const changes = typeof log.audit_json === 'string' ? JSON.parse(log.audit_json) : log.audit_json
                                  Object.keys(changes || {}).forEach(key => {
                                    if (changes[key].old !== undefined || changes[key].new !== undefined) {
                                      oldValue = changes[key].old
                                      newValue = changes[key].new
                                    }
                                  })
                                } catch { /* ignore malformed audit_json */ }
                                const actorName = log.created_by_name || log.created_by || 'System'
                                return (
                                  <div key={log.id || idx} className="px-3.5 py-2.5">
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{actorName}</span>
                                      <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                        {log.created_at ? formatDateTime(log.created_at) : ''}
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      {oldValue !== null && oldValue !== undefined && (
                                        <div className="flex items-start gap-2">
                                          <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 w-8 flex-shrink-0 pt-0.5">From</span>
                                          <span className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 px-2 py-0.5 rounded-md break-all">
                                            {String(oldValue) || '(empty)'}
                                          </span>
                                        </div>
                                      )}
                                      {newValue !== null && newValue !== undefined && (
                                        <div className="flex items-start gap-2">
                                          <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 w-8 flex-shrink-0 pt-0.5">To</span>
                                          <span className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 px-2 py-0.5 rounded-md break-all">
                                            {String(newValue) || '(empty)'}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </AnchoredPopover>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ── Bid Line Items (line records) ── */}
            <div
              ref={el => { fieldRowRefs.current['lines'] = el }}
              className={`p-1 -m-1 transition-shadow ${jumpHighlightClass('lines')}`}
            >
              <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                <BidLinesTable lines={lines} onChange={setLines} items={lookups.items || []} readOnly
                  headerInherit={headerInherit} onCellFocus={handleLineFocus} L={L} />
                {sourceHint?.key?.startsWith('line:') && <SourceHint hint={sourceHint} />}
              </EditGate>
            </div>

            {/* ── Server-side validation errors ── */}
            {!postResult?.success && serverErrors.length > 0 && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 p-3 animate-panel-in">
                <p className="text-xs font-semibold text-red-800 dark:text-red-200 mb-1.5">
                  NetSuite rejected {serverErrors.length} {serverErrors.length === 1 ? 'field' : 'fields'}
                </p>
                <ul className="space-y-1">
                  {serverErrors.map((e, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                      {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Unified pre-post checklist — one collapsible card instead of a
                permanently-open list, same design as the PO request page. ── */}
            {!postResult?.success && (() => {
              const ready = issues.length === 0
              return (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => !ready && setShowChecklist(o => !o)}
                    className={`w-full flex items-center gap-2 px-3.5 py-2.5 transition-colors ${
                      ready
                        ? 'bg-green-50 dark:bg-green-900/10 cursor-default'
                        : 'bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                    }`}
                  >
                    {ready
                      ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                      : <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />}
                    <p className={`text-sm font-semibold flex-1 text-left ${ready ? 'text-green-700 dark:text-green-300' : 'text-amber-800 dark:text-amber-300'}`}>
                      {ready
                        ? `Ready to post — ${linePayload.length} line ${linePayload.length === 1 ? 'item' : 'items'}`
                        : `${issues.length} thing${issues.length !== 1 ? 's' : ''} to fix before posting`}
                    </p>
                    {!ready && (
                      <ChevronDown className={`h-4 w-4 text-amber-400 transition-transform flex-shrink-0 ${showChecklist ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  {!ready && showChecklist && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800 animate-panel-in">
                      {issueGroups.map(({ group, items }) => (
                        <div key={group} className="px-3.5 py-2.5">
                          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                            {group} ({items.length})
                          </p>
                          <div className="space-y-0.5 -mx-1.5">
                            {items.map((item, i) => {
                              const clickable = item.keys && item.keys.length > 0
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => clickable && jumpToFieldKeys(item.keys)}
                                  disabled={!clickable}
                                  title={clickable ? `Jump to ${item.label}` : item.label}
                                  className={`w-full flex items-start justify-between gap-2 px-1.5 py-1 rounded text-left transition-colors ${
                                    clickable ? 'hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer' : 'cursor-default'
                                  }`}
                                >
                                  <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 min-w-0 flex-shrink-0 max-w-[40%]">
                                    <span className="truncate">{item.label}</span>
                                    {clickable && <ChevronRight className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                                  </span>
                                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300 text-right">{item.detail}</span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* ── Post result ── */}
            {postResult?.success && (
              <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-3 animate-fade-in">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-green-800 dark:text-green-200">
                    <p className="font-semibold">Special Pricing posted to NetSuite</p>
                    <p className="mt-0.5">
                      {postResult.bid_number && <span className="font-mono">{postResult.bid_number}</span>}
                      {postResult.lines_total ? ` · ${postResult.lines_created} of ${postResult.lines_total} line items` : ''}
                    </p>
                    {postResult.ns_url && (
                      <a href={postResult.ns_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 underline hover:no-underline">
                        Open in NetSuite <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {postResult && !postResult.success && (
              <div className={`rounded-lg border p-3 animate-fade-in ${postResult.partial
                ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10'
                : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${postResult.partial ? 'text-amber-600' : 'text-red-600'}`} />
                  <div className={`text-xs ${postResult.partial ? 'text-amber-800 dark:text-amber-200' : 'text-red-800 dark:text-red-200'}`}>
                    {postResult.partial ? (
                      <>
                        <p className="font-semibold">Bid created — some line items still need posting</p>
                        <p className="mt-0.5">
                          The bid and {postResult.lines_created} of {postResult.lines_total} line items were created in
                          NetSuite. Retry will add only the missing {(postResult.lines_total || 0) - (postResult.lines_created || 0)} —
                          nothing already created is duplicated.
                        </p>
                        {postResult.ns_url && (
                          <a href={postResult.ns_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-1 underline hover:no-underline">
                            Open the bid in NetSuite <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-semibold">Could not post this bid</p>
                        <p className="mt-0.5">{postResult.error}</p>
                      </>
                    )}
                    {postResult.line_errors?.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {postResult.line_errors.map((e, i) => (
                          <li key={i}>• Line {e.line}: {e.error}</li>
                        ))}
                      </ul>
                    )}
                    <p className="mt-1 opacity-75">Attempt {postResult.attempt} of {MAX_ATTEMPTS}</p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Actions ── */}
            {!postResult?.success && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex flex-col w-full max-w-md gap-2">
                  {postResult && !postResult.success ? (
                    <button onClick={handleRetry} disabled={posting || postAttempts >= MAX_ATTEMPTS || isReader}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow">
                      {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
                      {postResult.partial ? 'Retry — post the missing line items' : 'Retry posting'}
                    </button>
                  ) : (
                    <button onClick={handlePost} disabled={!canPost || posting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-navy-700 hover:bg-navy-800 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow">
                      {posting ? <><Loader2 className="h-4 w-4 animate-spin" /> Validating &amp; Sending…</> : <><Send className="h-4 w-4" /> Approve and Post Bid to NS</>}
                    </button>
                  )}
                </div>
                {!canPost && !posting && (
                  <button
                    type="button"
                    onClick={() => setShowChecklist(true)}
                    className="text-center text-xs text-amber-600 dark:text-amber-400 hover:underline"
                  >
                    Resolve the {issues.length} {issues.length === 1 ? 'issue' : 'issues'} above to enable posting
                  </button>
                )}
              </div>
            )}
            </div>
            )}
          </section>

          {/* Draggable divider — drag to resize Fields vs document panels */}
          <div
            onMouseDown={pdfPanelVisible ? handleDividerMouseDown : undefined}
            role="separator"
            aria-orientation="vertical"
            title="Drag to resize"
            className={`relative flex-shrink-0 bg-gray-200 dark:bg-gray-700 hover:bg-teal-400 dark:hover:bg-teal-500 active:bg-teal-500 group z-10 overflow-hidden ${
              isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
            } ${pdfPanelVisible ? 'w-1 opacity-100 cursor-col-resize' : 'w-0 opacity-0 pointer-events-none'}`}
          >
            <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
          </div>

          {/* RIGHT: Document viewer */}
          <div
            style={{ width: pdfPanelVisible ? `${100 - leftPanelPct}%` : '0%' }}
            className={`flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden ${
              isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
            } ${pdfPanelVisible ? 'min-w-[300px] opacity-100' : 'min-w-0 opacity-0 pointer-events-none'}`}
          >
            <div className="flex-1 overflow-hidden">
              {pdfDocId
                ? <PDFViewer documentId={pdfDocId} highlightedField={highlightedField} />
                : <div className="h-full flex items-center justify-center text-sm text-gray-400">No document attached</div>}
            </div>
          </div>

        </div>
      </div>

      {/* ── Posting progress modal ── */}
      {posting && (
        postMinimized ? (
          <button onClick={() => setPostMinimized(false)}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Posting bid…</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{postProgress.percent}% — click to expand</p>
            </div>
            <Maximize2 className="h-3.5 w-3.5 text-gray-400 ml-1" />
          </button>
        ) : (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setPostMinimized(true)} />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-lg w-full mx-4">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 opacity-10 animate-pulse" />
              <div className="relative flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg mb-4">
                  <Bot className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Posting Special Pricing to NetSuite</h3>
                <div className="w-full mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{postProgress.message || 'Starting…'}</span>
                    <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{postProgress.percent}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(postProgress.percent, 2)}%` }} />
                  </div>
                </div>
                <div className="w-full space-y-2 text-left">
                  {[
                    { label: 'Prepare fields', threshold: 5 },
                    { label: 'Validate against NS', threshold: 25 },
                    { label: 'Create bid record', threshold: 50 },
                    {
                      // The extra sub-step PO doesn't need: with a two-step write
                      // the line progress is real, user-relevant information.
                      label: postProgress.lineTotal
                        ? `Creating line items ${Math.min(postProgress.lineAt || 0, postProgress.lineTotal)} of ${postProgress.lineTotal}`
                        : 'Creating line items',
                      threshold: 90,
                    },
                    { label: 'Finalise & save', threshold: 100 },
                  ].map(step => {
                    const done = postProgress.percent >= step.threshold
                    const active = postProgress.percent >= step.threshold - 25 && postProgress.percent < step.threshold
                    return (
                      <div key={step.label} className="flex items-center gap-2">
                        {done ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                          : active ? <Loader2 className="h-3.5 w-3.5 text-indigo-500 animate-spin flex-shrink-0" />
                          : <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />}
                        <span className={`text-xs ${done ? 'text-green-600 dark:text-green-400 font-medium' : active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}>
                          {step.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <p className="mt-4 text-[10px] text-gray-400">Click outside to minimise</p>
              </div>
            </div>
          </div>
        )
      )}

      {/* ── Cancel Request Modal ── */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cancel Request</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason for cancellation <span className="text-red-500">*</span></label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Enter reason…"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason('') }}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Keep Request
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={!cancelReason.trim() || cancelling}
                className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                {cancelling ? 'Cancelling…' : 'Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
